/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
/* eslint-disable no-await-in-loop */
import React, {
  createContext, useContext, useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { normalizeScannerPayload } from './scannerPayload';

const ScannerContext = createContext();

export const useScanner = () => useContext(ScannerContext);

const SERIAL_ACK_STORAGE_KEY = 'scannerSerialAckEnabled';
const SCANNER_LOG_PREFIX = '[Scanner]';
const SERIAL_IDLE_FLUSH_MS = 100;

const scannerLog = (...args) => {
  console.log(SCANNER_LOG_PREFIX, ...args);
};

const scannerWarn = (...args) => {
  console.warn(SCANNER_LOG_PREFIX, ...args);
};

const scannerSelectionCancelled = (error) => (
  error?.name === 'NotFoundError'
  || /no (device|port) selected/i.test(error?.message || '')
);

const serialAckEnabled = () => {
  try {
    return window.localStorage.getItem(SERIAL_ACK_STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

function ScannerProvider({ children }) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const readerRef = useRef(null);
  const serialReadPromiseRef = useRef(null);
  const serialFlushTimerRef = useRef(null);
  const isCancelled = useRef(false);
  const chunks = useRef([]);
  const serialBuffer = useRef('');
  const callbackRef = useRef(null);

  const [device, setDevice] = useState(null);
  const [port, setPort] = useState(null); // Serial port state
  const [chunksCount, setChunksCount] = useState(0);

  // Function to add and remove input report listener for HID devices
  const addHIDDataListener = useCallback((handleInputReport) => {
    if (device) {
      device.addEventListener('inputreport', handleInputReport);
    }
  }, [device]);

  // Updated handleInputReport function
  const handleHIDData = useCallback((event) => {
    const { data } = event;
    let result = '';

    // Read data from the event and accumulate in a string
    for (let i = 0; i < data.byteLength; i += 1) {
      const charCode = data.getUint8(i);

      // Check if the character is printable ASCII, excluding '?'
      if ((charCode === 10 || (charCode >= 32 && charCode <= 126)) && charCode !== 63) {
        result += String.fromCharCode(charCode);
      }
    }

    // Push the result into the chunks array
    scannerLog('HID input report received', {
      bytes: data.byteLength,
      decoded: result,
    });
    chunks.current.push(result);
    setChunksCount(chunks.current.length);
  }, []);

  const reconnectScanner = useCallback(async (cb) => {
    if (!device && !port) {
      try {
        scannerLog('Reconnect requested');

        // Request HID devices first
        const hidDevices = await navigator.hid.getDevices({
          filters: [{ vendorId: 0x05E0 }, { vendorId: 0x05F9 }],
        });

        scannerLog('Previously-granted HID devices found', hidDevices.length);

        if (hidDevices.length > 0) {
          const selectedDevice = hidDevices[0];
          await selectedDevice.open();
          scannerLog('Reconnected HID device', {
            productName: selectedDevice.productName,
            vendorId: selectedDevice.vendorId,
            productId: selectedDevice.productId,
          });
          setDevice(selectedDevice);
        } else {
          // If no HID device, request a serial port
          const allPorts = await navigator.serial.getPorts();
          scannerLog('Previously-granted serial ports found', allPorts.length);
          if (allPorts.length === 0) {
            return;
          }
          const selectedPort = allPorts[0];
          scannerLog('Attempting serial reconnect', selectedPort.getInfo());
          if (selectedPort.isConnected || selectedPort.readable || selectedPort.writable) {
            scannerLog('Closing existing serial port before reconnect');
            await selectedPort.close();
          }
          await selectedPort.open({ baudRate: 115200 });
          scannerLog('Serial reconnect opened', selectedPort.getInfo());
          setPort(selectedPort);

          if (typeof cb === 'function') {
            cb(selectedPort);
          }
        }
      } catch (error) {
        scannerWarn('Reconnect failed silently', error);
        // Fail silently
      }
    }
  }, [device, port, addHIDDataListener, handleHIDData]);

  // Browser device choosers each require their own direct user action. Do not
  // attempt to open the Serial chooser after the HID chooser is dismissed.
  const connectScanner = useCallback(async (cb, transport = 'hid') => {
    if (!device && !port) {
      try {
        scannerLog('Connect requested', { transport });

        if (transport === 'hid') {
          const hidDevices = await navigator.hid.requestDevice({
            filters: [{ vendorId: 0x05E0 }, { vendorId: 0x05F9 }],
          });

          scannerLog('HID request returned devices', hidDevices.length);

          if (hidDevices.length === 0) return;

          const selectedDevice = hidDevices[0];
          await selectedDevice.open();
          scannerLog('HID device opened', {
            productName: selectedDevice.productName,
            vendorId: selectedDevice.vendorId,
            productId: selectedDevice.productId,
          });
          setDevice(selectedDevice);

          if (typeof cb === 'function') {
            cb(selectedDevice);
          }

          toastr.success(selectedDevice.productName, 'HID connected!');
          return;
        }

        if (transport === 'serial') {
          const selectedPort = await navigator.serial.requestPort();
          scannerLog('Serial port selected', selectedPort.getInfo());
          await selectedPort.open({ baudRate: 115200 });
          scannerLog('Serial port opened', selectedPort.getInfo());
          setPort(selectedPort);

          if (typeof cb === 'function') {
            cb(selectedPort);
          }

          toastr.success(selectedPort, 'Serial device connected!');
          return;
        }

        throw new Error(`Unsupported scanner transport: ${transport}`);
      } catch (error) {
        if (scannerSelectionCancelled(error)) {
          scannerLog('Scanner selection cancelled', { transport });
        } else {
          scannerWarn('Connect failed', error);
          toastr.error(error.message, 'Failed to connect to scanner');
        }
      }
    }
  }, [device, port, addHIDDataListener, handleHIDData]);

  const sendAcknowledgment = useCallback(async ({ silent = false } = {}) => {
    if (port && port.writable) {
      const writer = port.writable.getWriter();
      try {
        const ackMessage = new Uint8Array([0x06]); // Create a Uint8Array with the ACK byte
        scannerLog('Sending serial ACK');
        await writer.write(ackMessage); // Send the acknowledgment
      } catch (error) {
        scannerWarn('Failed to send serial ACK', error);
        toastr.error(error.message, 'Failed to send acknowledgment');
      } finally {
        await writer.releaseLock(); // Always release the lock
      }
    } else if (!silent) {
      toastr.warning('Port is not writable or not available');
    }
  }, [port]);

  const sendBeep = useCallback(async () => {
    if (port && port.writable) {
      const writer = port.writable.getWriter();
      try {
        const beepCommand = new Uint8Array([0x07]); // BEL character for beep
        scannerLog('Sending scanner beep command');
        await writer.write(beepCommand); // Send the beep command
        toastr.success('Beep sent to the scanner');
      } catch (error) {
        scannerWarn('Failed to send beep command', error);
        toastr.error(error.message, 'Failed to send beep');
      } finally {
        writer.releaseLock(); // Always release the lock
      }
    } else {
      toastr.warning('Port is not writable or not available');
    }
  }, [port]);

  // Handler function to process serial data
  const handleSerialData = (value) => {
    if (!(value instanceof Uint8Array)) {
      throw new Error('Received data is not a Uint8Array');
    }

    const decoder = new TextDecoder('utf-8');
    return decoder.decode(value); // Return decoded data
  };

  const clearSerialFlushTimer = useCallback(() => {
    if (serialFlushTimerRef.current) {
      window.clearTimeout(serialFlushTimerRef.current);
      serialFlushTimerRef.current = null;
    }
  }, []);

  const deliverSerialLabel = useCallback((label, reason) => {
    const data = normalizeScannerPayload(label);

    if (data && callbackRef.current) {
      scannerLog('Delivering serial scan to listener', {
        reason,
        data,
      });
      callbackRef.current(data); // Use the latest callback
    } else if (data) {
      scannerWarn('Serial scan parsed but no listener is registered', {
        reason,
        data,
      });
    } else {
      scannerLog('Ignoring empty serial label after trim', { reason });
    }
  }, []);

  const flushSerialBuffer = useCallback((reason) => {
    if (!serialBuffer.current) {
      scannerLog('Serial buffer flush skipped because buffer is empty', { reason });
      return;
    }

    const label = serialBuffer.current;
    serialBuffer.current = '';

    scannerLog('Flushing unterminated serial buffer', {
      reason,
      label,
    });
    deliverSerialLabel(label, reason);
  }, [deliverSerialLabel]);

  const scheduleSerialBufferFlush = useCallback(() => {
    clearSerialFlushTimer();
    serialFlushTimerRef.current = window.setTimeout(() => {
      serialFlushTimerRef.current = null;
      flushSerialBuffer('idle-timeout');
    }, SERIAL_IDLE_FLUSH_MS);
  }, [clearSerialFlushTimer, flushSerialBuffer]);

  const isConnected = () => device || port;

  const destroyReader = async () => {
    // Cleanup serial port and HID device
    scannerLog('Destroy reader requested', {
      hasReader: !!readerRef.current,
      hasDevice: !!device,
      hasPort: !!port,
    });
    isCancelled.current = true;

    const reader = readerRef.current;
    if (reader) {
      try {
        scannerLog('Cancelling serial reader');
        await reader.cancel();
      } catch (error) {
        scannerWarn('Serial reader cancel failed or stream already closed', error);
        // The stream may already be closed.
      }
    }

    if (serialReadPromiseRef.current) {
      try {
        scannerLog('Waiting for serial read loop to finish');
        await serialReadPromiseRef.current;
      } catch (error) {
        scannerWarn('Serial read loop finished with shutdown error', error);
        // Ignore read-loop shutdown errors while disconnecting.
      }
    }

    callbackRef.current = null;
    clearSerialFlushTimer();
    serialBuffer.current = '';
    if (device) {
      try {
        scannerLog('Closing HID device', {
          productName: device.productName,
          vendorId: device.vendorId,
          productId: device.productId,
        });
        await device.close();
      } catch (error) {
        scannerWarn('HID close failed or device already closed', error);
        // Ignore close errors while disconnecting.
      }
      setDevice(null);
    }
    if (port) {
      if (port.readable || port.writable) {
        try {
          scannerLog('Closing serial port', port.getInfo());
          await port.close();
        } catch (error) {
          scannerWarn('Serial close failed or port already closed', error);
          // Ignore close errors while disconnecting.
        }
      }
      setPort(null);
    }
  };

  // Function to handle data from serial port
  const readFromSerial = useCallback(async () => {
    if (port && port.readable) {
      let reader;
      let readError = null;
      try {
        scannerLog('Starting serial read loop', port.getInfo());
        reader = port.readable.getReader();
        readerRef.current = reader;
        isCancelled.current = false;

        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (isCancelled.current) {
            break; // Exit the loop if the component is unmounting
          }
          const { value, done } = await reader.read();

          if (value) {
            const decoded = handleSerialData(value);
            scannerLog('Serial chunk received', {
              bytes: value.length,
              decoded,
              raw: Array.from(value),
            });

            serialBuffer.current += decoded;

            const labels = serialBuffer.current.split(/\r\n|\r|\n/);
            serialBuffer.current = labels.pop() || '';

            scannerLog('Serial buffer parsed', {
              completeLabels: labels.length,
              remainingBuffer: serialBuffer.current,
            });

            labels.forEach((label) => deliverSerialLabel(label, 'line-ending'));

            if (serialBuffer.current) {
              scheduleSerialBufferFlush();
            } else {
              clearSerialFlushTimer();
            }

            if (serialAckEnabled()) {
              await sendAcknowledgment({ silent: true });
            } else {
              scannerLog('Serial ACK disabled');
            }
          }
          if (done) {
            scannerLog('Serial read loop done');
            if (!isCancelled.current) {
              readError = new Error('The scanner serial stream ended unexpectedly.');
            }
            break; // Stream is closed, exit the loop
          }
        }
      } catch (error) {
        if (!isCancelled.current) {
          readError = error;
          scannerWarn('Serial read loop error', error);
        }
      } finally {
        if (reader) {
          try {
            scannerLog('Releasing serial reader lock');
            await reader.releaseLock();
          } catch (error) {
            scannerWarn('Serial reader lock release failed or was already released', error);
            // The lock may already be released.
          }
        }
        if (readerRef.current === reader) {
          readerRef.current = null;
        }
        serialReadPromiseRef.current = null;

        if (readError && !isCancelled.current) {
          clearSerialFlushTimer();
          serialBuffer.current = '';

          if (port.readable || port.writable) {
            try {
              scannerLog('Closing failed serial connection', port.getInfo());
              await port.close();
            } catch (error) {
              scannerWarn('Failed serial connection could not be closed cleanly', error);
            }
          }

          setPort((currentPort) => (currentPort === port ? null : currentPort));
          toastr.error(
            'Reconnect it from the barcode scanner button.',
            'Scanner connection lost',
          );
        }
      }
    }
  }, [
    port,
    clearSerialFlushTimer,
    deliverSerialLabel,
    handleSerialData,
    scheduleSerialBufferFlush,
    sendAcknowledgment,
  ]);

  const removeHIDDataListener = useCallback(async (handleInputReport) => {
    if (device) {
      device.removeEventListener('inputreport', handleInputReport);
    }
  }, [device]);

  const removeListener = useCallback(async () => {
    scannerLog('Removing scan listener');
    callbackRef.current = null;
    // isCancelled.current = true;
    await removeHIDDataListener(handleHIDData);
  }, []);

  const registerListener = useCallback(async (handler) => {
    scannerLog('Registering scan listener', {
      hasPort: !!port,
      hasReader: !!readerRef.current,
    });
    callbackRef.current = handler; // Store the handler in the ref

    // Ensure the reader is initialized if not already running
    if (port && !readerRef.current) {
      scannerLog('Starting serial read loop after listener registration');
      serialReadPromiseRef.current = readFromSerial();
    }
  }, [port, readFromSerial]);

  // Cleanup when context is unmounted
  useEffect(() => () => {
    (async () => {
      await destroyReader();
    })();
  }, []);

  useEffect(() => {
    if (chunksCount > 0) {
      let accumulatedData = ''; // Variable to store accumulated data

      // Combine all the chunks into accumulatedData
      chunks.current.forEach((chunkData) => {
        accumulatedData += chunkData;
      });

      // Split accumulatedData by newlines
      const labels = accumulatedData.split('\n');

      // Iterate through each label except the last one (which might not be complete)
      labels.slice(0, -1).forEach((label) => {
        const normalizedLabel = normalizeScannerPayload(label);

        if (!normalizedLabel) return;

        if (typeof callbackRef.current === 'function') {
          scannerLog('Delivering HID scan to listener', normalizedLabel);
          callbackRef.current(normalizedLabel); // Run the callback function with the label
        } else {
          scannerWarn('HID scan parsed but no listener is registered', normalizedLabel);
        }
      });

      // The last part of the split could be an incomplete label, so keep it
      const lastLabel = labels[labels.length - 1];
      chunks.current = lastLabel ? [lastLabel] : [];

      // Update chunk count based on remaining incomplete label
      setChunksCount(chunks.current.length);
    }
  }, [chunksCount]);

  useEffect(() => {
    if (device) {
      scannerLog('Adding HID input listener');
      addHIDDataListener(handleHIDData);
    }
    // Cleanup on unmount
    return () => {
      if (device) {
        scannerLog('Removing HID input listener');
        removeHIDDataListener(handleHIDData);
      }
    };
  }, [device, addHIDDataListener, handleHIDData]);

  useEffect(() => {
    if (port && callbackRef.current && !readerRef.current) {
      scannerLog('Starting serial read loop after port became available');
      serialReadPromiseRef.current = readFromSerial();
    }
  }, [port, readFromSerial]);

  const contextValue = useMemo(() => ({
    reconnectScanner,
    connectScanner,
    registerListener,
    removeListener,
    isConnected,
    destroyReader,
  }), [
    reconnectScanner, connectScanner, registerListener, removeListener, isConnected, destroyReader,
  ]);

  return (
    <ScannerContext.Provider value={contextValue}>
      {children}
    </ScannerContext.Provider>
  );
}

ScannerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ScannerProvider;
